<template>
  <div class="mb-3 card ">
    <div class="card-header bg-secondary-3">
      <div v-if="value.area.letter" class="bg-green rounded letter-box mr-2">
        {{ value.area.letter | empty }}
      </div>
      Áreas de interés:
      <b>
        {{ value.area.name | empty }}
        <span v-if="isEvaluatedMainTasks">:</span>
      </b>
      <span v-if="value.area.description">
        {{ value.area.description | empty }}
      </span>
    </div>
    <div class="card-body bg-secondary-3">
      <div class="row">
        <div class="col-md-1">
          <label class="data-name">Puntuación:</label>
          <validation-provider
            ref="score"
            v-slot="{ errors, failedRules }"
            :name="`Puntuación-${value.area.id}`"
            rules="required|between:0,100"
          >
            <custom-input
              ref="scoreInput"
              v-model="scoreText"
              class="w-100 "
              :custom-error-msg="getErrorMessage(failedRules)"
              :disabled="isReadOnly"
              :error="errors[0]"
              placeholder="0-100"
              @input="changeScore"
            />
          </validation-provider>
        </div>
        <div class="col-md-2">
          <label class="data-name">Prioridad:</label>
          <validation-provider v-slot="{ errors }" rules="required">
            <custom-radio
              v-model="hasPriority"
              class="row"
              custom-error-msg="El campo es requerido."
              :disabled="isReadOnly"
              :error="errors[0]"
              is-inline
              :name="`has-priority-${value.area.id}`"
              :options="yesNotOptions"
            />
          </validation-provider>
        </div>
        <div class="col-md">
          <label class="data-name">Interpretación:</label>
          <custom-input v-model="model.comment" :disabled="isReadOnly" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationProvider } from 'vee-validate';
import CustomInput from 'CustomInput';
import CustomRadio from 'custom-radio';

import RADIO_OPTIONS from '@/utils/constants/customRadioOptions';
import Categories from '@/utils/constants/vocationalEvaluationInstrumentAreaCategory.js';

export default {
  name: 'AreaItem',
  components: {
    ValidationProvider,
    CustomInput,
    CustomRadio,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scoreText: '',
    };
  },
  computed: {
    model: {
      get() {
        return this.value || {};
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    hasPriority: {
      get() {
        if (![true, false].includes(this.model.hasPriority)) return null;
        return this.model.hasPriority
          ? this.yesNotOptions[0]
          : this.yesNotOptions[1];
      },
      set({ value }) {
        this.model.hasPriority = value;
      },
    },
    getErrorMessage() {
      return (failedRules) => {
        if (!failedRules) return null;
        if (failedRules.required) return 'El campo es requerido.';
        if (failedRules.between) return 'El valor debe estar entre 0 y 100.';
        return null;
      };
    },
    isEvaluatedMainTasks() {
      return this.value.area?.category === Categories.EvaluatedMainTasks;
    },
    yesNotOptions() {
      return RADIO_OPTIONS;
    },
  },
  watch: {
    'model.score': function(val) {
      const current = this.scoreText === '' ? null : Number(this.scoreText);
      if (val !== current) {
        this.scoreText = val === null || val === undefined ? '' : String(val);
      }
    },
  },
  created() {
    this.scoreText =
      this.model.score === null || this.model.score === undefined
        ? ''
        : String(this.model.score);
  },
  methods: {
    // Strips anything that isn't a digit or a single decimal point, so letters
    // can never end up in model.score (they used to be silently coerced to
    // NaN/null by Number(v), and an out-of-range value here was previously
    // only caught by the vee-validate "between" rule, which is skipped on a
    // plain "Guardar" draft save — the invalid value reached the API as-is
    // and the DB update threw. Clamping to [0, 100] here guarantees a safe
    // value regardless of whether the form is validated before saving.
    sanitizeScore(value) {
      let s = String(value == null ? '' : value).replace(/[^\d.]/g, '');
      const dot = s.indexOf('.');
      if (dot !== -1) {
        s =
          s.slice(0, dot + 1) +
          s
            .slice(dot + 1)
            .replace(/\./g, '')
            .slice(0, 1);
      }
      return s;
    },
    changeScore(v) {
      const raw = String(v == null ? '' : v);
      const clean = this.sanitizeScore(v);
      const num = parseFloat(clean);
      const clamped = Number.isNaN(num)
        ? null
        : Math.min(100, Math.max(0, num));

      this.scoreText = clamped === null ? clean : String(clamped);
      this.model.score = clamped;

      if (raw !== this.scoreText) {
        this.$nextTick(() => {
          const input = this.$refs.scoreInput;
          if (input && typeof input.$forceUpdate === 'function') {
            input.$forceUpdate();
          }
        });
      }
    },
  },
};
</script>

<style scoped>
.letter-box {
  width: 30px;
  height: 30px;
  line-height: 30px;
  display: inline-block;
  text-align: center;
  color: white;
  font-weight: bold;
}

::v-deep
  .custom-control-input:checked:disabled
  ~ .custom-control-label::before {
  background-color: #3f5b60 !important;
  border-color: #3f5b60 !important;
}
</style>
